import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide for Pledge Department Management page.
 * Targets search, add new department button, and department list (expand to add roles/positions).
 */
export const usePledgeDepartmentManagementTourGuide = () => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnDeptManagement = pathname === ROUTE_NAMES.DEPARTMENT_MANAGEMENT;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "pledgeDepartmentManagement",
      enabled: isOnDeptManagement,
      steps: [
        {
          id: "pledge-dept-mgmt-search",
          targetSelector: '[data-tour-item="pledge-dept-mgmt-search"]',
          title: t("tourGuide.pledgeDepartmentManagement.search.title"),
          description: t(
            "tourGuide.pledgeDepartmentManagement.search.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-dept-mgmt-add-dept",
          targetSelector: '[data-tour-item="pledge-dept-mgmt-add-dept"]',
          title: t("tourGuide.pledgeDepartmentManagement.addDept.title"),
          description: t(
            "tourGuide.pledgeDepartmentManagement.addDept.description",
          ),
          position: "bottom",
          offset: { x: 0, y: 15 },
          highlightPadding: 10,
        },
        {
          id: "pledge-dept-mgmt-department-list",
          targetSelector: '[data-tour-item="pledge-dept-mgmt-department-list"]',
          title: t("tourGuide.pledgeDepartmentManagement.departmentList.title"),
          description: t(
            "tourGuide.pledgeDepartmentManagement.departmentList.description",
          ),
          position: "top",
          offset: { x: 0, y: -15 },
          highlightPadding: 12,
        },
      ],
    }),
    [isOnDeptManagement, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            {
              type: TourGuideType.PledgeDepartmentManagement,
              isDone: true,
            },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const pledgeDepartmentManagementTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.PledgeDepartmentManagement,
    isOnTourPath: isOnDeptManagement && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (pledgeDepartmentManagementTourGuide.isActive) {
      setActiveTourModule("pledgeDepartmentManagement");
      setCurrentTourStep(pledgeDepartmentManagementTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    pledgeDepartmentManagementTourGuide.isActive,
    pledgeDepartmentManagementTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    pledgeDepartmentManagementTourGuide,
    isMobile,
  };
};
